import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import copy from 'copy-to-clipboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram } from '@fortawesome/free-brands-svg-icons/faInstagram';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons/faWhatsapp';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons/faXTwitter';
import { faQrcode } from '@fortawesome/pro-light-svg-icons/faQrcode';
import { TypedMemo } from '../../common/types';
import { openExternal } from '../../../utils/openExternal';
import styles from './SMLPublishedShare.module.scss';
import { trackEvent } from '../../analytics/sml/analytics';

interface SMLPublishedShareProps {
  shareUrl: string;
  /** What the message says beside the address. */
  shareTitle: string;
  isQROpen: boolean;
  onToggleQR: () => void;
}

/**
 * The four places a page goes in its first minute.
 *
 * WhatsApp and X take a real share url. Instagram has no web composer for a
 * story, so the chip does the only honest thing: it hands the link to the
 * system share sheet where there is one, and otherwise copies it and says so.
 */
const SMLPublishedShareComponent: React.FC<SMLPublishedShareProps> = ({
  shareUrl,
  shareTitle,
  isQROpen,
  onToggleQR,
}) => {
  const intl = useIntl();
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  const onInstagram = useCallback(() => {
    if (typeof navigator.share === 'function') {
      navigator.share({ title: shareTitle, url: shareUrl }).catch(console.error);
      return;
    }
    copy(shareUrl);
    setIsLinkCopied(true);
    trackEvent('link_copied', { kind: 'smartlink' });
  }, [shareUrl, shareTitle]);

  const onWhatsApp = useCallback(() => {
    openExternal(`https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${shareUrl}`)}`).catch(console.error);
  }, [shareUrl, shareTitle]);

  const onX = useCallback(() => {
    openExternal(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`
    ).catch(console.error);
  }, [shareUrl, shareTitle]);

  return (
    <div className={styles.share}>
      <div className={styles.chips}>
        <button type="button" className={styles.chip} onClick={onInstagram}>
          <FontAwesomeIcon icon={faInstagram} />
          {intl.formatMessage({ id: 'app.sml.published.share.instagram', defaultMessage: 'Story' })}
        </button>
        <button type="button" className={styles.chip} onClick={onWhatsApp}>
          <FontAwesomeIcon icon={faWhatsapp} />
          {intl.formatMessage({ id: 'app.sml.published.share.whatsapp', defaultMessage: 'WhatsApp' })}
        </button>
        <button type="button" className={styles.chip} onClick={onX}>
          <FontAwesomeIcon icon={faXTwitter} />
          {intl.formatMessage({ id: 'app.sml.published.share.x', defaultMessage: 'X' })}
        </button>
        <button type="button" className={clsx(styles.chip, isQROpen && styles.chipOn)} onClick={onToggleQR}>
          <FontAwesomeIcon icon={faQrcode} />
          {intl.formatMessage({ id: 'app.sml.published.share.qr', defaultMessage: 'QR code' })}
        </button>
      </div>
      {isLinkCopied && (
        <p className={styles.hint}>
          {intl.formatMessage({
            id: 'app.sml.published.share.instagram_hint',
            defaultMessage: 'Link copied. Instagram takes it in the story sticker — paste it there.',
          })}
        </p>
      )}
    </div>
  );
};

export const SMLPublishedShare = TypedMemo(SMLPublishedShareComponent);
